import React from 'react';
import PropTypes from 'prop-types';
import { LocalForm, Control, actions } from 'react-redux-form';
import ReactSuperSelect from 'react-super-select';
import requiredIf from 'react-required-if';
import SuperSelectComponent from './SuperSelectComponent';
import SelectComponent from './SelectComponent';
import ConditionalComponent from './ConditionalComponent';
import {
  Checkbox,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Panel,
  Row
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import Moment from 'moment';
import {
  CASE_METRICS_TOGGLES, pageTypes as constants, pageTypes,
  componentHelper,
  metricsPagesHelper,
  colours,
  scenarios as scenarioHelpers,
  unicode
} from './../helpers';
import SetMetricsByDateModalContainer from '../Containers/SetMetricsByDateModalContainer';

let dateModel;

export default class MetricsSelectorComponent extends React.Component {
  static propTypes = {
    metricsByDateInfo: PropTypes.shape({
      timestamp: PropTypes.number.isRequired,
      runCount: PropTypes.number.isRequired
    }).isRequired,
    metricsInfo: PropTypes.shape({
      toggles: PropTypes.number.isRequired,
      buildFilter: PropTypes.string.isRequired,
      nodes: PropTypes.arrayOf(PropTypes.string).isRequired,
      image: requiredIf(PropTypes.string, props =>
        props.pageType === constants.CASEMETRICS ||
         props.pageType === constants.ENGINEMETRICS),
      caseName: requiredIf(PropTypes.string, props =>
        props.pageType === constants.PERFORMANCEMETRICS),
      action: requiredIf(PropTypes.string, props =>
        props.pageType === constants.PERFORMANCEMETRICS),
      dispatcherId: PropTypes.number.isRequired
    }).isRequired,
    availableNodes: PropTypes.array.isRequired,
    availableImages: requiredIf(PropTypes.array, props =>
      props.pageType === constants.CASEMETRICS ||
      props.pageType === constants.ENGINEMETRICS),
    availableCaseActions: requiredIf(PropTypes.array, props =>
      props.pageType === constants.PERFORMANCEMETRICS),
    availableCaseActionsByDispatcher: requiredIf(PropTypes.array, props =>
      props.pageType === constants.PERFORMANCEMETRICS),
    availableDispatchers: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      number: PropTypes.number.isRequired,
      scenarioCount: PropTypes.number.isRequired
    })),
    dropdownPairFirstSelected: PropTypes.string.isRequired,
    analyzedStatusesByName: PropTypes.objectOf(PropTypes.string).isRequired,
    onSelectorChange: PropTypes.func.isRequired,
    onDateSelected: PropTypes.func.isRequired,
    onControlUse: PropTypes.func.isRequired,
    pageType: PropTypes.shape({ type: PropTypes.string.isRequired })
  }

  _setModel = (value) => {
    dateModel = value;
  }

  // pulls out the Local's forms dispatcher for use
  attachDispatch = (dispatch) => this.formDispatch = dispatch;

  // succeeds whenever the node array === ['all'], (when coming from other pages)
  checkResetNodes = (nodes) => {
    if (!this.formDispatch) { return; }
    if (nodes.length === 1 && nodes[0] === 'all') {
      this.formDispatch(actions.change('info.nodes', ['Select All']));
    }
  }

  setMetricsByDate = (timestamp) => {
    this.formDispatch(actions.change('info.timestamp', timestamp));
    this.setState({ confirmationModalAcknowledged: true });
  }

  getImages = () => this.props.availableImages;

  getCases = () => {
    const caseActions = this.props.availableCaseActionsByDispatcher.length > 0 ?
      this.props.availableCaseActionsByDispatcher : this.props.availableCaseActions;
    const cases = new Set();
    caseActions.forEach(caseAction => {
      cases.add(caseAction.split('|')[0]);
    });
    return Array.from(cases);
  }

  getActions = (caseName) => {
    const caseActions = this.props.availableCaseActions;
    const actions = new Set();
    caseActions.forEach(caseAction => {
      if (caseAction.split('|')[0] === caseName) {
        actions.add(caseAction.split('|')[1]);
      }
    });
    return Array.from(actions);
  }

  getInitialDispatcherOption = (id, pageType) => {
    const dispatcher = this.props.availableDispatchers.find(d => d.id === id);
    const text = pageType === constants.PERFORMANCEMETRICS ?
      'Scenarios' :
      'Images';
    return {
      id,
      number: dispatcher ? dispatcher.number : 0,
      name: dispatcher ?
        `${dispatcher.number} - (${text}:${dispatcher.scenarioCount})` :
        id === 0 ? 'Select a Dispatcher...' : 'Loading Dispatchers',
      jobId: dispatcher ? dispatcher.jobId : 0
    };
  }

  getDispatcherOptions = (pageType) => this.props.availableDispatchers.map(
    d => {
      const text = pageType === constants.PERFORMANCEMETRICS ?
        'Scenarios' :
        'Images';
      return {
        id: d.id,
        number: d.number,
        jobId: d.jobId,
        name: `${d.number} - (${text}:${d.scenarioCount}) ${d.name}`
      };
    }
  );

  customImageOptionTemplater = (props) => (option) => {
    const scanName = option.name;
    const analyzedStatus = props.analyzedStatusesByName[scanName];

    let statusPrefix = null;
    switch (analyzedStatus) {
      case scenarioHelpers.SCENARIO_COMPLETED: {
        statusPrefix = (
          <span style={{ color: colours.colours.blue }}>
            {unicode.CHECK_MARK}
          </span>
        );
        break;
      }
      case scenarioHelpers.SCENARIO_ERROR: {
        statusPrefix = (
          <span style={{ color: colours.colours.red }}>
            {unicode.WARNING}
          </span>
        );
        break;
      }
      case scenarioHelpers.SCENARIO_FAILED:
        statusPrefix = (
          <span style={{ color: colours.colours.red }}>
            {unicode.RED_CROSS_MARK}
          </span>
        );
        break;
      default:
        break;
    }

    const template = (
      <div style={{ display: 'inline' }}>
        {statusPrefix}
        &nbsp;
        <span>{option.name}</span>
      </div>
    );
    return template;
  };

  constructor(props) {
    super(props);
    this.state = { confirmationModalAcknowledged: false };
  }

  render() {
    const {
      metricsByDateInfo,
      metricsInfo,
      onSelectorChange,
      pageType,
      onDateSelected,
      dropdownPairFirstSelected
    } = this.props;

    const initialState =
      {
        timestamp: metricsByDateInfo.timestamp,
        runCount: metricsByDateInfo.runCount,
        toggles: metricsInfo.toggles,
        buildFilter: metricsInfo.buildFilter,
        nodes: metricsInfo.nodes,
        image: metricsInfo.image ? metricsInfo.image : '',
        caseName: metricsInfo.caseName ? metricsInfo.caseName : '',
        action: metricsInfo.action ? metricsInfo.action : '',
        dispatcherId: metricsInfo.dispatcherId,
      };

    return (
      <div>
        <fieldset>
          <legend> Filters </legend>
          <SetMetricsByDateModalContainer
            model={dateModel}
            formCallback={this.setMetricsByDate}
            pageType={pageType}
          />
          <LocalForm
            id="metrics-selector-form"
            getDispatch={dispatch => this.attachDispatch(dispatch)}
            model="info"
            initialState={initialState}
            onChange={model => {
              onSelectorChange(model);
              this._setModel(model);
            }}
          >
            <FormGroup>
              <Row>
                <Col md={3}>
                  { pageType === constants.PERFORMANCEMETRICS ?
                    <ControlLabel> Performance Run Since </ControlLabel> :
                    <ControlLabel> Images Run Since </ControlLabel>
                  }
                  <Control.custom
                    id="datepicker-input"
                    model=".timestamp"
                    component={DatePicker}
                    className="form-control"
                    mapProps={({ modelValue, onChange }) => {
                      return {
                        dateFormat: 'MMM Do, YYYY',
                        selected: Moment.unix(modelValue),
                        onSelect: date => {
                          this._setModel({ ...dateModel, timestamp: date.unix(date) });
                          this.state.confirmationModalAcknowledged ?
                            onChange(date.unix(date)) :
                            onDateSelected();
                        },
                        maxDate: Moment()
                      };
                    }}
                    fixedHeight
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={10}
                    shouldCloseOnSelect={false}
                  />
                </Col>
                <Col md={2}>
                  <ControlLabel> Runs to Show </ControlLabel>
                  <Control
                    id="run-count-input"
                    model=".runCount"
                    className="form-control"
                    component={FormControl}
                    type="number"
                  />
                </Col>
                <Col md={3}>
                  <ControlLabel> Build Types </ControlLabel>
                  <div>
                    <Control.custom
                      id="prod-type-check"
                      component={Checkbox}
                      model=".toggles"
                      mapProps={({ modelValue, onChange }) => {
                        const value = CASE_METRICS_TOGGLES.PROD;
                        return {
                          onChange: c =>
                            onChange(componentHelper.getTogglesValue(
                              modelValue,
                              value,
                              c.target.checked
                            )),
                          checked: componentHelper.getChecked(modelValue, value)
                        };
                      }}
                      inline
                    >
                      Prod
                    </Control.custom>
                    <Control.custom
                      id="prebuild-type-check"
                      component={Checkbox}
                      model=".toggles"
                      mapProps={({ modelValue, onChange }) => {
                        const value = CASE_METRICS_TOGGLES.PREBUILD;
                        return {
                          onChange: c =>
                            onChange(componentHelper.getTogglesValue(
                              modelValue,
                              value,
                              c.target.checked
                            )),
                          checked: componentHelper.getChecked(modelValue, value)
                        };
                      }}
                      inline
                    >
                      Prebuild
                    </Control.custom>
                    <Control.custom
                      id="topic-type-check"
                      component={Checkbox}
                      model=".toggles"
                      mapProps={({ modelValue, onChange }) => {
                        const value = CASE_METRICS_TOGGLES.TOPIC;
                        return {
                          onChange: c =>
                            onChange(componentHelper.getTogglesValue(
                              modelValue,
                              value,
                              c.target.checked
                            )),
                          checked: componentHelper.getChecked(modelValue, value)
                        };
                      }}
                      inline
                    >
                      Topic
                    </Control.custom>
                  </div>
                </Col>
                <Col md={1}>
                  <ControlLabel> Include </ControlLabel>
                  <div>
                    <Control.custom
                      id="outlier-type-check"
                      component={Checkbox}
                      model=".toggles"
                      mapProps={({ modelValue, onChange }) => {
                        const value = CASE_METRICS_TOGGLES.OUTLIER;
                        return {
                          onChange: c =>
                            onChange(componentHelper.getTogglesValue(
                              modelValue,
                              value,
                              c.target.checked
                            )),
                          checked: componentHelper.getChecked(modelValue, value)
                        };
                      }}
                      inline
                    >
                      Outliers
                    </Control.custom>
                    <ConditionalComponent
                      condition={pageType === pageTypes.CASEMETRICS}
                    >
                      <Control.custom
                        id="artifact-only-check"
                        component={Checkbox}
                        model=".toggles"
                        mapProps={({ modelValue, onChange }) => {
                          const value = CASE_METRICS_TOGGLES.ARTIFACT_ONLY;
                          return {
                            onChange: c =>
                              onChange(componentHelper.getTogglesValue(
                                modelValue,
                                value,
                                c.target.checked
                              )),
                            checked: componentHelper.getChecked(modelValue, value)
                          };
                        }}
                      >
                        Artifact Only
                      </Control.custom>
                    </ConditionalComponent>
                  </div>
                </Col>
                <Col md={2}>
                  <ControlLabel> Topic Branch / Build / Dispatcher </ControlLabel>
                  <Control.custom
                    id="build-input"
                    model="."
                    className="form-control"
                    component={FormControl}
                    type="text"
                    mapProps={({ modelValue, onChange }) => {
                      return {
                        value: modelValue.buildFilter,
                        onChange: i => {
                          const model = {
                            ...modelValue,
                            buildFilter: i.target.value
                          };
                          onChange(model);
                        }
                      };
                    }}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={11}>
                  <Panel>
                    <Panel.Body>
                      <Col>
                        <ControlLabel>
                          {dropdownPairFirstSelected === metricsPagesHelper.IMAGE_MARKER ?
                            <div>
                              Dispatcher&nbsp;
                              <span style={{ color: colours.colours.blue }}>
                                (filtered)
                              </span>
                            </div> :
                            <div>Dispatcher</div>
                          }
                        </ControlLabel>
                        <Control.custom
                          id="dispatcher-select"
                          model="."
                          component={ReactSuperSelect}
                          className="form-control"
                          style={{ marginRight: '10px', 'marginBottom': '20px' }}
                          mapProps={({ modelValue, onChange }) => {
                            return {
                              initialValue:
                                this.getInitialDispatcherOption(
                                  modelValue.dispatcherId, pageType),
                              noneOption: 'Select a Dispatcher...',
                              dataSource: this.getDispatcherOptions(pageType),
                              onChange: option => {
                                const filter = option && option.number !== 0 ?
                                  `d:${option.number}` :
                                  '';
                                const model = {
                                  ...modelValue,
                                  buildFilter: filter,
                                  dispatcherId: option ?
                                    option.id :
                                    modelValue.dispatcherId
                                };
                                return onChange(model);
                              },
                              onClear: () => {
                                const model = {
                                  ...modelValue,
                                  buildFilter: '',
                                  dispatcherId: 0
                                };
                                return onChange(model);
                              }
                            };
                          }}
                          searchable
                        />
                        <ConditionalComponent
                          condition={pageType === constants.CASEMETRICS ||
                            pageType === constants.ARTIFACTSMETRICS ||
                            pageType === constants.ENGINEMETRICS}
                        >
                          <ControlLabel>
                            {dropdownPairFirstSelected ===
                            metricsPagesHelper.DISPATCHER_MARKER ?
                              <div>
                                Image&nbsp;
                                <span style={{ color: colours.colours.blue }}>
                                  (filtered)
                                </span>
                              </div> :
                              <div>Image</div>
                            }
                          </ControlLabel>
                          <Control.custom
                            id="image-select"
                            model="."
                            component={SuperSelectComponent}
                            className="form-control"
                            style={{ marginRight: '10px' }}
                            mapProps={({ modelValue, onChange }) => {
                              return {
                                initialValue: modelValue.image,
                                onChange: selectedOption => {
                                  const model = {
                                    ...modelValue,
                                    image: selectedOption
                                  };
                                  return onChange(model);
                                }
                              };
                            }}
                            options={this.getImages()}
                            noneOption={'Select an Image...'}
                            searchable
                            customOptionTemplateFunction={
                              this.customImageOptionTemplater(this.props)
                            }
                          />
                        </ConditionalComponent>
                      </Col>
                    </Panel.Body>
                  </Panel>
                </Col>
              </Row>
              { pageType === constants.PERFORMANCEMETRICS &&
              (<Row>
                <Col md={5}>
                  <ControlLabel> Case </ControlLabel>
                  <Control.custom
                    id="case-select"
                    model=".caseName"
                    component={SelectComponent}
                    className="form-control"
                    style={{ marginRight: '10px' }}
                    mapProps={({ modelValue, onChange }) => {
                      return {
                        noneOption: 'Select a Case...',
                        options: this.getCases(),
                        selected: modelValue,
                        onChange: v => onChange(v)
                      };
                    }}
                  />
                </Col>
                <Col md={6}>
                  <ControlLabel> Action </ControlLabel>
                  <Control.custom
                    id="action-select"
                    model="."
                    component={SelectComponent}
                    className="form-control"
                    style={{ marginRight: '10px' }}
                    mapProps={({ modelValue, onChange }) => {
                      return {
                        noneOption: 'Select an Action...',
                        options: this.getActions(modelValue.caseName),
                        selected: modelValue.action,
                        onChange: d => {
                          const model = {
                            ...modelValue,
                            action: d
                          };
                          onChange(model);
                        }
                      };
                    }}
                  />
                </Col>
              </Row>)}
            </FormGroup>
          </LocalForm>
        </fieldset>
      </div>
    );
  }
}


// WEBPACK FOOTER //
// src/Components/MetricsSelectorComponent.jsx